// packages/ui/src/components/security/SecurityStatsRow.tsx
//
// Wave 12 Task 104 — 4 stat cards row per design PNG.

import type { JSX } from 'react';
import { cn } from '@printway-global/design-system';
import type { SecurityStats } from './SecurityCenterPage.js';

export interface SecurityStatsRowProps {
  stats: SecurityStats;
}

interface StatProps {
  label: string;
  value: number;
  tone: 'green' | 'yellow' | 'red' | 'gray';
  hint: string;
}

const TONE: Record<StatProps['tone'], { iconBg: string; iconText: string }> = {
  green: { iconBg: 'bg-green-100', iconText: 'text-green-600' },
  yellow: { iconBg: 'bg-yellow-100', iconText: 'text-yellow-600' },
  red: { iconBg: 'bg-red-100', iconText: 'text-red-600' },
  gray: { iconBg: 'bg-pw-surface', iconText: 'text-pw-text-muted' },
};

function StatCard({ label, value, tone, hint }: StatProps): JSX.Element {
  const t = TONE[tone];
  return (
    <div className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-pw-text-muted">{label}</p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-pw-text">
            {value}
          </p>
        </div>
        <div
          aria-hidden="true"
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md',
            t.iconBg,
            t.iconText,
          )}
        >
          {/* Generic shield icon */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </div>
      </div>
      <p className="mt-3 text-xs text-pw-text-muted">{hint}</p>
    </div>
  );
}

export function SecurityStatsRow({ stats }: SecurityStatsRowProps): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Active Sessions"
        value={stats.activeSessions}
        tone="green"
        hint="Currently signed in"
      />
      <StatCard
        label="Failed Attempts"
        value={stats.failedAttempts}
        tone={stats.failedAttempts > 0 ? 'yellow' : 'gray'}
        hint="Last 24 hours"
      />
      <StatCard
        label="Blocked Attempts"
        value={stats.blockedAttempts}
        tone={stats.blockedAttempts > 0 ? 'red' : 'gray'}
        hint="Auto-blocked by rules"
      />
      <StatCard
        label="API Keys Active"
        value={stats.apiKeysActive}
        tone="gray"
        hint="Manage in Settings"
      />
    </div>
  );
}

export default SecurityStatsRow;
